import * as Msal from '@azure/msal-browser';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import _log from '@/helpers/log';

type RuntimeEnv = Record<string, string | undefined>;

function getRuntimeEnv(): RuntimeEnv {
  // prefer injected Vite env (globalThis.__VITE_ENV__), else fall back to process.env
  return (
    (globalThis as any).__VITE_ENV__ ??
    (typeof process !== 'undefined'
      ? (process.env as unknown as RuntimeEnv)
      : {})
  );
}

export const createAuthenticationService = ({
  MsalBuilder = Msal.PublicClientApplication,
  errorHandler = _log.logError
} = {}) => {
  let AuthAgent: Msal.PublicClientApplication | undefined;
  let redirectResponse: Promise<Msal.AuthenticationResult | null> | undefined;

  function getConfigFromEnv() {
    const env = getRuntimeEnv();
    const clientId = env.VITE_AD_clientId ?? env.VUE_APP_AD_clientId;
    const graphScope = env.VITE_AD_graphScopes ?? env.VUE_APP_AD_graphScopes;
    const authorityUrl =
      env.VITE_AD_authorityUrl ?? env.VUE_APP_AD_authorityUrl;
    const tenantId = env.VITE_AD_tenantId ?? env.VUE_APP_AD_tenantId;
    const signInAuthority =
      authorityUrl && tenantId ? `${authorityUrl}/${tenantId}` : undefined;
    return { clientId, graphScope, authorityUrl, tenantId, signInAuthority };
  }

  async function init(): Promise<Msal.AuthenticationResult | null> {
    // Lazy validation: do not throw at module import time
    const { clientId, graphScope, signInAuthority } = getConfigFromEnv();

    if (!clientId || !graphScope || !signInAuthority) {
      // If env missing, throw here so callers know — but only when init is called.
      throw new Error(
        'Incomplete environment variables for authentication (VITE_AD_*)'
      );
    }

    if (!redirectResponse) {
      AuthAgent = new MsalBuilder({
        auth: {
          clientId,
          authority: signInAuthority,
          knownAuthorities: [signInAuthority],
          redirectUri: window.location.origin
        },
        cache: {
          cacheLocation: 'sessionStorage',
          storeAuthStateInCookie: false
        }
      });

      redirectResponse = AuthAgent.handleRedirectPromise()
        .catch(() => null)
        .then(async(response) => {
          if (!isLoggedIn()) {
            await login();
          }
          return response;
        });
    }
    return redirectResponse;
  }

  async function login(): Promise<void> {
    if (!AuthAgent) {
      await init();
    }
    // Acquire interactive redirect (no return value)
    return AuthAgent!.acquireTokenRedirect({
      scopes: ['openid', 'profile']
    } as any);
  }

  async function getToken(): Promise<string | void> {
    await init();
    try {
      const cfg = getConfigFromEnv();
      const { graphScope } = cfg;
      const { accessToken } = await AuthAgent!.acquireTokenSilent({
        scopes: [graphScope!],
        account: await getUser()
      } as any);
      return accessToken;
    } catch (error) {
      errorHandler('Cannot generate access token', error);
      if (error instanceof InteractionRequiredAuthError) {
        await login();
      }
      throw error;
    }
  }

  async function logout(): Promise<void> {
    await init();
    return AuthAgent!.logoutRedirect();
  }

  async function getUser(): Promise<Msal.AccountInfo | undefined> {
    await init();
    return AuthAgent!.getAllAccounts()[0];
  }

  function isLoggedIn(): boolean {
    return !!AuthAgent && AuthAgent.getAllAccounts()[0] !== undefined;
  }

  return {
    getUser,
    getToken,
    logout
  };
};

export default createAuthenticationService();
